import React, { useState, useEffect } from "react";
import "./onboardingAdmin.css";

const API_BASE_URL = process.env.NODE_ENV === 'development' 
    ? 'http://127.0.0.1:8080'
    : 'https://bymolive-backend-995926394015.us-central1.run.app';

const ProfilesTable = ({ formData, fetchForms }) => {
    const [editedData, setEditedData] = useState(formData || []);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [instagramProfile, setInstagramProfile] = useState("");
    const [loading, setLoading] = useState(false);
    const [programs, setPrograms] = useState([]);
    const [editingRows, setEditingRows] = useState({});
    const [userData, setUserData] = useState({}); // Store user data from the API
    const [lastDataRefresh, setLastDataRefresh] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);

    useEffect(() => {
        // Check if we have data in session storage and when it was last refreshed
        const lastRefreshTime = sessionStorage.getItem('profilesLastRefresh');
        if (lastRefreshTime) {
            setLastDataRefresh(new Date(lastRefreshTime));
        }
        
        // Load data from session storage or fetch if not available
        loadDataFromSessionOrFetch();
    }, []);

    const loadDataFromSessionOrFetch = () => {
        let shouldFetchProfiles = true;
        let shouldFetchPrograms = true;
        
        // Try to load profiles from session storage
        const cachedProfiles = sessionStorage.getItem('cachedProfiles');
        if (cachedProfiles) {
            try {
                const profiles = JSON.parse(cachedProfiles);
                setEditedData(profiles);
                shouldFetchProfiles = false;
                console.log("Loaded profiles from session storage");
                
                // Load user data associated with these profiles
                const cachedUserData = sessionStorage.getItem('cachedUserData');
                if (cachedUserData) {
                    setUserData(JSON.parse(cachedUserData));
                    console.log("Loaded user data from session storage");
                }
            } catch (error) {
                console.error("Error loading profiles from session storage:", error);
            }
        }
        
        // Try to load programs from session storage
        const cachedPrograms = sessionStorage.getItem('cachedPrograms');
        if (cachedPrograms) {
            try {
                const programsData = JSON.parse(cachedPrograms);
                setPrograms(programsData);
                shouldFetchPrograms = false;
                console.log("Loaded programs from session storage");
            } catch (error) {
                console.error("Error loading programs from session storage:", error);
            }
        }
        
        // Fetch data if not found in session storage
        if (shouldFetchProfiles) {
            fetchProfiles();
        }
        
        if (shouldFetchPrograms) {
            fetchPrograms();
        }
    };
    
    // Function to fetch all data and store in session storage
    const refreshAllData = async () => {
        setIsRefreshing(true);
        setSuccessMessage("Refreshing data...");
        
        try {
            // Fetch profiles
            await fetchProfiles(true);
            
            // Fetch programs
            await fetchPrograms(true);
            
            // Set last refresh time
            const now = new Date();
            sessionStorage.setItem('profilesLastRefresh', now.toISOString());
            setLastDataRefresh(now);
            
            setSuccessMessage("Data refreshed successfully!");
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) {
            setErrorMessage("Error refreshing data: " + error.message);
            setTimeout(() => setErrorMessage(""), 5000);
        } finally {
            setIsRefreshing(false);
        }
    };

    // Modify fetchProfiles to store in session storage if requested
    const fetchProfiles = async (storeInSession = false) => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/onboarding/profile`);
            if (response.ok) {
                const data = await response.json();
                // Sort profiles by creation date, newest first
                const sortedProfiles = (data.profiles || []).sort((a, b) => {
                    const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
                    const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
                    return dateB - dateA;
                });
                
                setEditedData(sortedProfiles);
                
                // Store in session storage if requested
                if (storeInSession) {
                    sessionStorage.setItem('cachedProfiles', JSON.stringify(sortedProfiles));
                }
                
                // Fetch user data for these profiles
                await fetchUserDataForProfiles(sortedProfiles, storeInSession);
            } else {
                const errorData = await response.json();
                console.error("Failed to fetch profiles:", errorData);
                setErrorMessage("Failed to fetch profiles");
            }
        } catch (err) {
            console.error("Error fetching profiles:", err);
            setErrorMessage("Error fetching profiles");
        } finally {
            setLoading(false);
        }
    };

    // Modified to store in session if requested
    const fetchPrograms = async (storeInSession = false) => {
        try {
            console.log("Fetching programs from:", `${API_BASE_URL}/api/programs`);
            const response = await fetch(`${API_BASE_URL}/api/programs`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            console.log("Programs API response:", data);

            if (data && data.programs) {
                const formattedPrograms = data.programs.map(program => ({
                    id: program.id,
                    name: program.name || program.id
                }));
                
                setPrograms(formattedPrograms);
                
                // Store in session storage if requested
                if (storeInSession) {
                    sessionStorage.setItem('cachedPrograms', JSON.stringify(formattedPrograms));
                }
            } else {
                console.error("Unexpected programs data format:", data);
                setPrograms([]);
            }
        } catch (err) {
            console.error("Error fetching programs:", err);
            setErrorMessage("Error fetching programs");
        }
    };

    // Modified to store in session if requested
    const fetchUserDataForProfiles = async (profiles = editedData, storeInSession = false) => {
        const userDataMap = {};
        
        for (const profile of profiles) {
            if (profile.email && profile.email !== "Adresa ta de Email") {
                try {
                    const response = await fetch(`${API_BASE_URL}/api/onboarding/get-user-data?email=${encodeURIComponent(profile.email)}`);
                    
                    if (response.ok) {
                        const data = await response.json();
                        if (data.user_exists && data.documents) {
                            // Find the 00info document
                            const infoDoc = data.documents.find(doc => doc.document_id === "00info");
                            if (infoDoc) {
                                userDataMap[profile.profileName] = infoDoc;
                            }
                        }
                    }
                } catch (err) {
                    console.error(`Error fetching user data for ${profile.email}:`, err);
                }
            }
        }
        
        setUserData(userDataMap);
        
        // Store in session storage if requested
        if (storeInSession) {
            sessionStorage.setItem('cachedUserData', JSON.stringify(userDataMap));
        }
    };

    const handleAddProfile = async () => {
        if (!instagramProfile.trim()) {
            return;
        }

        try {
            // Get the latest program from the current programs list
            const latestProgram = programs.length > 0 ? programs[0] : null;

            const response = await fetch(
                `${API_BASE_URL}/api/onboarding/profile`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        profileName: instagramProfile,
                        fullName: "X",
                        email: "X",
                        phone: "X",
                        address: "Adresa unde poti primi colete BYMO",
                        code: "XXX",
                        program: latestProgram ? latestProgram.id : "X",
                        formFilled: "No"
                    }),
                }
            );

            if (response.ok) {
                setInstagramProfile("");
                setSuccessMessage(`Profile "${instagramProfile}" added successfully!`);
                setTimeout(() => setSuccessMessage(""), 3000);
                fetchProfiles();
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to add profile");
            }
        } catch (err) {
            setErrorMessage(`Error adding profile: ${err.message}`);
            setTimeout(() => setErrorMessage(""), 5000);
            console.error("An error occurred while adding the profile.", err);
        }
    };

    // Modified handleFieldChange to track changes
    const handleFieldChange = (index, field, value) => {
        setEditedData((prevData) =>
            prevData.map((row, rowIndex) =>
                rowIndex === index ? { ...row, [field]: value } : row
            )
        );
        // Mark this row as edited
        setEditingRows(prev => ({
            ...prev,
            [index]: true
        }));
    };

    // New function to handle single row save
    const handleSaveRow = async (index, form) => {
        try {
            const updateData = {
                profileName: form.profileName,
                program: form.program || "X",
                // Only include program-related fields since we're only updating the program
                formFilled: "Yes"
            };

            console.log(`Updating program for profile ${form.profileName}:`, updateData);

            const response = await fetch(
                `${API_BASE_URL}/api/onboarding/profile`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(updateData),
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to update profile ${form.profileName}`);
            }

            const responseData = await response.json();
            console.log("Update response:", responseData);

            // Clear the editing state for this row
            setEditingRows(prev => ({
                ...prev,
                [index]: false
            }));

            setSuccessMessage(`Program updated for ${form.profileName}`);
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (err) {
            setErrorMessage(`Error updating program: ${err.message}`);
            setTimeout(() => setErrorMessage(""), 5000);
            console.error("Error updating program:", err);
        }
    };

    // Modified renderProgramDropdown to include save button
    const renderProgramDropdown = (form, index) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <select
                value={form.program || "X"}
                onChange={(e) => handleFieldChange(index, "program", e.target.value)}
            >
                <option value="X">Not Assigned</option>
                {programs.length > 0 ? (
                    programs.map((program) => (
                        <option key={program.id} value={program.id}>
                            {program.name}
                        </option>
                    ))
                ) : (
                    <option disabled>Loading programs...</option>
                )}
            </select>
            {editingRows[index] && (
                <button
                    onClick={() => handleSaveRow(index, form)}
                    className="save-row-button"
                    style={{
                        padding: '4px 8px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Save
                </button>
            )}
        </div>
    );

    // Keep the handleSaveChanges for other fields...
    const handleSaveChanges = async () => {
        try {
            for (const form of editedData) {
                if (!form.profileName) {
                    throw new Error("Profile name is required for saving changes.");
                }

                // Only save rows that haven't been individually saved
                const rowIndex = editedData.findIndex(row => row.profileName === form.profileName);
                if (!editingRows[rowIndex]) {
                    continue;
                }

                const updateData = {
                    profileName: form.profileName,
                    fullName: form.fullName || "",
                    email: form.email || "",
                    code: form.code || "",
                    program: form.program || "X",
                    phone: form.phone || "",
                    address: form.address || "",
                    formFilled: "Yes"
                };

                console.log("Updating profile with data:", updateData);

                const response = await fetch(
                    `${API_BASE_URL}/api/onboarding/profile`,
                    {
                        method: "PUT",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(updateData),
                    }
                );

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || `Failed to update profile ${form.profileName}`);
                }

                const responseData = await response.json();
                console.log("Update response:", responseData); // Debug log
            }
            // Clear all editing states after bulk save
            setEditingRows({});
            setSuccessMessage("Changes saved successfully!");
            setTimeout(() => setSuccessMessage(""), 3000);
            fetchProfiles(); // Refresh the data
        } catch (err) {
            setErrorMessage(`Error saving changes: ${err.message}`);
            setTimeout(() => setErrorMessage(""), 5000);
            console.error("Error saving changes:", err);
        }
    };

    // Delete profile function
    const handleDeleteProfile = async (profileName) => {
        try {
            if (window.confirm(`Are you sure you want to delete the profile "${profileName}"?`)) {
                const response = await fetch(
                    `${API_BASE_URL}/api/onboarding/profile?profile=${encodeURIComponent(profileName)}`,
                    {
                        method: "DELETE",
                    }
                );

                if (!response.ok) {
                    throw new Error(`Failed to delete profile ${profileName}`);
                }

                setSuccessMessage(`Profile "${profileName}" deleted successfully!`);
                setTimeout(() => setSuccessMessage(""), 3000);
                fetchProfiles();
            }
        } catch (err) {
            setErrorMessage(`Error deleting profile: ${err.message}`);
            setTimeout(() => setErrorMessage(""), 5000);
            console.error("Error deleting profile:", err);
        }
    };

    // Helper function to format date
    const formatDate = (timestamp) => {
        if (!timestamp) return "N/A";
        const date = new Date(timestamp);
        return date.toLocaleDateString('ro-RO'); // Romanian date format
    };

    return (
        <div className="admin-onboarding-container">
            <div className="admin-onboarding-content">
                {/* Data Refresh Controls */}
                <div className="data-refresh-controls">
                    <button 
                        onClick={refreshAllData} 
                        disabled={isRefreshing}
                        className="refresh-data-button"
                    >
                        {isRefreshing ? "Refreshing..." : "Refresh All Data"}
                    </button>
                    {lastDataRefresh && (
                        <span className="last-refresh-time">
                            Last refreshed: {lastDataRefresh.toLocaleString()}
                        </span>
                    )}
                </div>
                
                {/* Success/Error Messages */}
                {successMessage && <p className="success-message">{successMessage}</p>}
                {errorMessage && <p className="error-message">{errorMessage}</p>}

                <div className="admin-onboard-table-top">
                    {loading && <p>Loading...</p>}

                    <div className="admin-insta-input-overall">
                        <div className="admin-insta-input">
                            <input
                                type="text"
                                placeholder="Add Instagram Profile"
                                value={instagramProfile}
                                onChange={(e) => setInstagramProfile(e.target.value)}
                            />
                            <button onClick={handleAddProfile}>Add Profile</button>
                        </div>
                        <button onClick={handleSaveChanges} className="save-changes-button">
                            Save All Changes
                        </button>
                    </div>

                    <table className="admin-onboard-table">
                        <thead>
                            <tr>
                                <th>Created</th>
                                <th>Signed Date</th>
                                <th>Instagram Profile</th>
                                <th>Full Name</th>
                                <th>Email</th>
                                <th>Code</th>
                                <th>Program</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                        {editedData && editedData.length > 0 ? (
                            editedData.map((form, index) => {
                                const userInfo = userData[form.profileName] || {};
                                return (
                                    <tr key={index}>
                                        <td>{formatDate(form.createdAt)}</td>
                                        <td>{userInfo["Signed date"] || "Not signed"}</td>
                                        <td>
                                            <a
                                                href={`https://instagram.com/${form.profileName}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {form.profileName}
                                            </a>
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                value={userInfo["Name"] || form.fullName || ""}
                                                onChange={(e) => handleFieldChange(index, "fullName", e.target.value)}
                                                readOnly={!!userInfo["Name"]}
                                                className={userInfo["Name"] ? "filled-data" : ""}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="email"
                                                value={userInfo["email"] || form.email || ""}
                                                onChange={(e) => handleFieldChange(index, "email", e.target.value)}
                                                readOnly={!!userInfo["email"]}
                                                className={userInfo["email"] ? "filled-data" : ""}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                value={userInfo["Cod Reducere"] || form.code || ""}
                                                onChange={(e) => handleFieldChange(index, "code", e.target.value)}
                                                readOnly={!!userInfo["Cod Reducere"]}
                                                className={userInfo["Cod Reducere"] ? "filled-data" : ""}
                                            />
                                        </td>
                                        <td>
                                            {renderProgramDropdown(form, index)}
                                        </td>
                                        <td>
                                            <button
                                                onClick={() => handleDeleteProfile(form.profileName)}
                                                className="delete-profile-button"
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="8" style={{ textAlign: "center" }}>
                                    No profiles found.
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ProfilesTable; 